import { rollupUniqueCommands } from "./selectors";
import flatMap from "lodash/flatMap";

// ----- Sorting helpers -----
const collator = new Intl.Collator("en", {
	sensitivity: "base",
	numeric: true,
	ignorePunctuation: true
});

const getNodeName = (n) => n?.name ?? n?.entityData?.properties?.name ?? "";

// Groups first, then alphabetical/numeric by name, then by id for stability
const compareNodes = (a, b) => {
	const aKind = a?.isGroup ? 0 : 1;
	const bKind = b?.isGroup ? 0 : 1;
	if (aKind !== bKind) return aKind - bKind;
	const nameCmp = collator.compare(getNodeName(a), getNodeName(b));
	if (nameCmp !== 0) return nameCmp;
	return (a?.id || "").localeCompare(b?.id || "", "en", { numeric: true });
};

// Recursively sort children at every node (no root special-casing)
const sortHierarchy = (node) => {
	const arr = node?.children;
	if (!Array.isArray(arr) || arr.length < 2) return node;
	arr.sort(compareNodes);
	arr.forEach(sortHierarchy);
	return node;
};

export const buildHierarchy = (inputData, devices, ignoreEmptyGroups, addUngroupedDevicesToRoot) => {
	const root = {
		id: "root",
		name: "All Devices",
		children: [],
		isGroup: true
	};

	const groupMap = new Map();
	groupMap.set(root.id, root);

	// Create group nodes
	Object.values(inputData).forEach((group) => {
		const groupNode = {
			id: group.id,
			name: group.name,
			children: [],
			isGroup: true,
			...group
		};

		const groupDevices = group?.devices?.map((device) => device.id) || [];
		const hasChildNodes = Object.values(devices).find((item) => groupDevices.includes(item.id));

		if (ignoreEmptyGroups && !hasChildNodes) return;
		groupMap.set(group.id, groupNode);

		// Attach to root if no parentGroupId
		if (!group.parentGroupId) {
			root.children.push(groupNode);
		}
	});

	// Add devices and establish parent-child relationships
	Object.values(inputData).forEach((group) => {
		const currentNode = groupMap.get(group.id);
		if (!currentNode) return;
		const deviceCommands = [];

		// Add devices as children
		(group.devices || []).forEach((device) => {
			if (devices[device.id]) {
				const deviceNode = {
					isDevice: true,
					isGroup: false,
					...devices[device.id]
				};

				// Avoid duplicate devices
				if (!currentNode.children.some((child) => child.id === device.id)) {
					// Combining device commands and added as deviceCommands to group nodes, to be used if needed.
					const commands = deviceNode?.entityData?.properties?.commands || [];
					if (deviceNode?.deviceCommands) {
						deviceCommands.push(...deviceNode.deviceCommands);
					} else if (commands?.length) {
						const filteredCommands = commands
							.flat()
							.filter((item) => typeof item === "object" && !Array.isArray(item))
							.map((command) => ({
								...command,
								entityIds: [deviceNode?.id]
							}));
						deviceCommands.push(...filteredCommands);
					}
					currentNode.children.push(deviceNode);
				}
			}
		});

		if (deviceCommands.length) currentNode.deviceCommands = rollupUniqueCommands(deviceCommands);

		// Link to parent group
		if (group.parentGroupId && groupMap.has(group.parentGroupId)) {
			const parentNode = groupMap.get(group.parentGroupId);
			if (!parentNode.children.some((child) => child.id === currentNode.id)) {
				// A combined list of all device commands to be added to root node, to be used if needed.
				const parentCommands = parentNode?.deviceCommands || [];
				if (deviceCommands.length)
					parentNode.deviceCommands = rollupUniqueCommands([...parentCommands, ...deviceCommands]);
				parentNode.children.push(currentNode);
			}
		}
	});

	// Assigning ungrouped devices to root, if addUngroupedDevicesToRoot prop is enabled.
	if (addUngroupedDevicesToRoot) addUngroupedEntitiesToRoot(inputData, devices, root);

	sortHierarchy(root);

	return root;
};

const addUngroupedEntitiesToRoot = (inputData, devices, root) => {
	const deviceNodes = Object.values(devices)?.map((device) => ({ ...device, isDevice: true, isGroup: false }));
	let ungroupedDevices = [];

	// If device groups exist, filter out devices that are not part of any device group and add them to root. If there are no device groups, add all devices to root.
	if (Object.values(inputData)?.length) {
		// Collecting all grouped device ids
		const groupedDevices = flatMap(
			Object.values(inputData).map((group) => group?.devices?.map((device) => device.id)) || []
		);

		// Iterating over devices to filter ungrouped devices.
		deviceNodes?.map((device) => {
			const alreadyExists = root.children.find((item) => item.id === device.id);
			if (!groupedDevices.includes(device.id) && !alreadyExists) ungroupedDevices.push(device);
		});
	} else {
		ungroupedDevices = deviceNodes;
	}

	if (ungroupedDevices.length) {
		const deviceCommands = [];

		ungroupedDevices.map((device) => {
			const commands = device?.entityData?.properties?.commands || [];
			if (device?.deviceCommands) {
				deviceCommands.push(...device.deviceCommands);
			} else if (commands?.length) {
				const filteredCommands = commands
					.flat()
					.filter((item) => typeof item === "object" && !Array.isArray(item))
					.map((command) => ({
						...command,
						entityIds: [device?.id]
					}));
				deviceCommands.push(...filteredCommands);
			}
		});

		root.children.push(...ungroupedDevices);
		root.deviceCommands = rollupUniqueCommands(deviceCommands);
	}
};
